import React from 'react';
import moment from 'moment';
import { find, get, includes, isEmpty, keys } from 'lodash';
import { analytics, analyticsActions, Track } from 'angieslist-app-platform';
import { Alert, Button, Col, DropdownButton, MenuItem, OverlayTrigger, Popover, Row, ReduxForm } from 'angieslist-react-components';
import { reduxForm, Form, getFormValues } from 'redux-form';
import { CategoryList } from 'angieslist-composed-components';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import { defaultPageProps } from '../../../../app/browser/analyticsDefaultProperties';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import profileActions from '../../common/profileActions';
import licenseActions from '../../common/licenseActions';
import CategoryFilter from './CategoryFilter';
import LicenseOrgField from './LicenseOrgField';
import './EditLicenseInformation.scss';

type Props = {
  change: Function,
  profile: Object,
  license: Object,
  uuid: string,
  maybeEditLicenseInfoId?: number,
  // eslint-disable-next-line react/no-unused-prop-types
  formValues: {
    firstName?: string,
    lastName?: string,
    companyName?: string,
    categories: Array<Object>
  },
  initialValues: {
    firstName?: string,
    lastName?: string,
    companyName?: string,
    categories: Array<Object>,
    licenseLocale?: string,
    licenseExpiration: undefined,
  },
  serviceProviderId: number,
  editCompletionFunction: Function,
  handleSubmit: Function,
  getCategoryAutocomplete: typeof profileActions.getCategoryAutocomplete,
  createTradeLicenseInfo: typeof profileActions.createTradeLicenseInfo,
  updateTradeLicenseInfo: typeof profileActions.updateTradeLicenseInfo,
  sendEnhancedTrackEvent: typeof analyticsActions.sendEnhancedTrackEvent,
  getAtlasOrgsByState: typeof licenseActions.getAtlasOrgsByState,
  clearAtlasOrgs: typeof licenseActions.clearAtlasOrgs,
  getAtlasOrgFields: typeof licenseActions.getAtlasOrgFields,
  clearAtlasOrgFields: typeof licenseActions.clearAtlasOrgFields,
  updateLicenseReset: typeof profileActions.updateLicenseReset,
  createLicenseReset: typeof profileActions.createLicenseReset,
};

const licenseSignatures = {
  'Not Licensed': 'I am not appropriately licensed for my trade.',
  'Not Applicable': 'Not applicable, my trade does not require licensing.',
  'In Progress': 'My license application is in progress.',
  Licensed: 'Yes, I am appropriately licensed for my trade.',
};

const redundantOrgFields = ['LicenseNumber', 'State'];

const validateCertification = (values, { registeredFields }) => {
  const additionalFieldNames = keys(registeredFields)
    .filter(name => name.startsWith('additionalFields.'))
    .map(name => name.slice(17));
  const errors = { additionalFields: {} };
  if (values.licenseSignature !== 'Not Applicable' && isEmpty(values.licenseExpiration)) {
    errors.licenseExpiration = 'Please provide license expiration to continue.';
  }
  if (values.verify) {
    if (isEmpty(values.licenseLocale)) {
      errors.licenseLocale = 'Please select a state to continue.';
    }
    if (isEmpty(values.licensingAuthorityExternalId)) {
      errors.licensingAuthorityExternalId = 'Please select a Certifying Organization to continue.';
    }
    if ((isEmpty(values.firstName) || isEmpty(values.lastName)) && isEmpty(values.companyName)) {
      errors.companyName = 'Please provide first name and last name or provide the company name.';
    }
    if (isEmpty(values.licenseNumber)) {
      errors.licenseNumber = 'Please provide license number to continue.';
    }
    additionalFieldNames.forEach((fieldName) => {
      if (!get(values, `additionalFields.${fieldName}`)) {
        errors.additionalFields[fieldName] = 'Please provide additional license data to continue.';
      }
    });
  }
  return errors;
};

class EditOrCreateLicenseInformation extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.state = {
      selectedCategories: get(props.formValues, 'categories', []),
    };
  }

  componentDidMount() {
    const {
      initialValues, clearAtlasOrgs, clearAtlasOrgFields, change,
    } = this.props;
    clearAtlasOrgs();
    clearAtlasOrgFields();
    change('licenseAuthorityManualText', get(initialValues, 'licensingAuthorityName', ''));
    const locale = get(initialValues, 'licenseLocale');
    if (locale) {
      this.loadOrgs(locale);
    }

    const externalOrgId = get(initialValues, 'licensingAuthorityExternalId');
    if (externalOrgId) {
      this.loadOrgFields(externalOrgId);
    }
  }

  // if states are slow to load, make sure we go back and re-attempt to load orgs
  componentDidUpdate({ license: prevLicense, profile: prevProfile }: Props) {
    const {
      license, initialValues, profile, editCompletionFunction,
      updateLicenseReset, createLicenseReset,
    } = this.props;
    const orgStates = get(license, 'getAtlasOrgStates.response.states', []);
    const prevOrgStates = get(prevLicense, 'getAtlasOrgStates.response.states', []);
    if (orgStates !== prevOrgStates) {
      const locale = get(initialValues, 'licenseLocale');
      if (locale) {
        this.loadOrgs(locale);
      }
    }

    const errorSaving = get(profile, 'createLicense.error', undefined) || get(profile, 'updateLicense.error', undefined);
    const prevError = get(prevProfile, 'createLicense.error', undefined) || get(prevProfile, 'updateLicense.error', undefined);
    if (errorSaving && errorSaving !== prevError) {
      window.scrollTo(0, 250);
    }

    const shouldRedirect = get(profile, 'createLicense.response.success', undefined) || get(profile, 'updateLicense.response.success', undefined);

    if (shouldRedirect) {
      // clear status before redirecting
      updateLicenseReset();
      createLicenseReset();
      editCompletionFunction();
    }
  }

  safeSaveString = (formField) => {
    const { formValues } = this.props;
    const formValue = get(formValues, [formField], '');
    return formValue.trim().length === 0 ? undefined : formValue;
  }

  saveLicense = async (data) => {
    const {
      createTradeLicenseInfo,
      formValues,
      maybeEditLicenseInfoId,
      serviceProviderId,
      updateTradeLicenseInfo,
      uuid,
    } = this.props;
    const submitVerification = get(formValues, 'verify', false);

    const tradeLicenseInfoUnified = {
      verifyLicense: submitVerification,
      categories: this.state.selectedCategories,
      licenseName: this.safeSaveString('licenseName'),
      firstName: this.safeSaveString('firstName'),
      lastName: this.safeSaveString('lastName'),
      company: this.safeSaveString('companyName'),
      licenseNumber: this.safeSaveString('licenseNumber'),
      expirationDate: formValues.licenseExpiration && !submitVerification ? moment(formValues.licenseExpiration).format('YYYY-MM-DD') : undefined,
      state: this.safeSaveString('licenseLocale'),
      trade: formValues.licenseSignature,
    };

    if (submitVerification) {
      if (formValues.licensingAuthorityName) {
        const additionalFields = formValues.additionalFields || {};

        if (additionalFields.LicenseType) {
          tradeLicenseInfoUnified.trade = additionalFields.LicenseType;
        }

        tradeLicenseInfoUnified.licenseAuthority = this.safeSaveString('licensingAuthorityName');
        tradeLicenseInfoUnified.licensingAuthorityExternalId = this.safeSaveString('licensingAuthorityExternalId');
      }
    } else {
      const licenseAuthority = this.safeSaveString('licenseAuthorityManualText');
      if (licenseAuthority) {
        tradeLicenseInfoUnified.licenseAuthority = licenseAuthority;
      }
    }

    if (maybeEditLicenseInfoId) {
      tradeLicenseInfoUnified.licenseId = maybeEditLicenseInfoId;
      return updateTradeLicenseInfo(serviceProviderId, uuid, tradeLicenseInfoUnified);
    }
    return createTradeLicenseInfo(serviceProviderId, uuid, tradeLicenseInfoUnified);
  }

  categoryAutocomplete = (query) => {
    const { getCategoryAutocomplete, serviceProviderId } = this.props;
    if (query) {
      getCategoryAutocomplete(serviceProviderId, query, 10);
    }
    this.setState(() => ({ categoryAutcompleteText: query }));
  }

  selectSuggestion = (category) => {
    const newCategory = {
      id: category.categoryId,
      name: category.displayText,
    };
    this.setState({ selectedCategories: [...this.state.selectedCategories, newCategory] });
  }

  updateState = (stateName) => {
    const { change, clearAtlasOrgs, clearAtlasOrgFields } = this.props;
    clearAtlasOrgFields();
    if (stateName && stateName.trim().length > 0) {
      this.loadOrgs(stateName);
    } else {
      clearAtlasOrgs();
    }
    change('licensingAuthorityExternalId', null);
    change('licensingAuthorityName', null);
    change('additionalFields', {});
  }

  loadOrgs = (stateName) => {
    const { license, getAtlasOrgsByState, serviceProviderId } = this.props;
    const orgStates = get(license, 'getAtlasOrgStates.response.states', []);
    const maybeOrgStateEntry = find(orgStates, ['name', stateName]);
    if (maybeOrgStateEntry) {
      getAtlasOrgsByState(serviceProviderId, maybeOrgStateEntry.id);
    }
  }

  updateOrg = (orgId) => {
    const { license, change, clearAtlasOrgFields } = this.props;
    clearAtlasOrgFields();
    change('additionalFields', null);
    const orgs = get(license, 'getAtlasOrgsByState.response.certifyingOrgs', []);
    const maybeOrg = find(orgs, ['id', orgId]);
    if (maybeOrg) {
      change('licensingAuthorityName', maybeOrg.name);
    }
    this.loadOrgFields(orgId);
  }

  loadOrgFields = (orgId) => {
    const { getAtlasOrgFields, serviceProviderId } = this.props;
    if (orgId) {
      getAtlasOrgFields(serviceProviderId, orgId);
    }
  }

  trackSaveEvent = () => {
    const { sendEnhancedTrackEvent, maybeEditLicenseInfoId, formValues } = this.props;
    const enteredLicenseNumber = get(formValues, 'licenseNumber', '');
    sendEnhancedTrackEvent({
      name: 'Item Clicked',
      properties: {
        ...defaultPageProps,
        activityLocation: 'SP : My Business : License Details',
        description: maybeEditLicenseInfoId ? 'Save Edited License' : 'Save New License',
        licenseNumber: enteredLicenseNumber,
      },
    });
  }

  render() {
    const {
      editCompletionFunction,
      change,
      formValues,
      maybeEditLicenseInfoId,
      profile,
      license,
      sendEnhancedTrackEvent,
      handleSubmit,
      updateLicenseReset,
      createLicenseReset,
    } = this.props;
    const currentCategories = this.state.selectedCategories;
    const autcompleteCategories = get(profile, 'getCategoryAutocomplete.response.categories', []);
    const selectableCategories = autcompleteCategories.filter(cat =>
      !currentCategories.map(current => current.id).includes(cat.categoryId));
    const isSaving = get(profile, 'createLicense.isLoading', false) || get(profile, 'updateLicense.isLoading', false);
    const errorSaving = get(profile, 'createLicense.error', undefined) || get(profile, 'updateLicense.error', undefined);
    const statesLoading = get(license, 'getAtlasOrgStates.isLoading', false);
    const orgStates = get(license, 'getAtlasOrgStates.response.states', []);
    const orgsLoading = get(license, 'getAtlasOrgsByState.isLoading', false);
    const atlasOrgs = get(license, 'getAtlasOrgsByState.response.certifyingOrgs', []);
    const orgFields = get(license, 'getAtlasOrgFields.response.fields', []);
    const orgFieldsLoading = get(license, 'getAtlasOrgFields.isLoading', false);
    // Enable when Get service has licenseStatus
    // const isAdvertiser=get(profile,'currentProfileUAPI.response.businessInfo.advertiser',false);
    const verificationActive = get(formValues, 'verify', false);

    const licenses = get(profile, 'currentProfileUAPI.response.businessInfo.licenses', []);
    const currentLicense = licenses.find(tradeLicense =>
      tradeLicense.licenseId === maybeEditLicenseInfoId) || {};
    const verificationPending = includes(currentLicense.verificationStatuses, 'Awaiting Verification');
    const successfullyVerified = includes(currentLicense.verificationStatuses, 'Verified');
    const pendingOrVerified = successfullyVerified || verificationPending;

    const popover = (
      <Popover id="Edit-LicenseInformation--Verify-License-Popover">
        Enabling this will verify this license with Atlas Certified. We may ask for additional
        information to verify this license.
      </Popover>
    );
    return (
      <Form onSubmit={handleSubmit(this.saveLicense)}>
        <div className="page-name">
          <span className="title">Add License</span>
          {/* Change false to isAdvertiser when Get service has licenseStatus */}
          <If condition={false}>
            <div className="pull-right Edit-License-Information--Verify-License-Group" >
              <Track>
                <ReduxForm.Input
                  id="Edit-License-Information--Verify-License-Checkbox"
                  type="checkbox"
                  name="verify"
                  label="Verify this license"
                  disabled={pendingOrVerified}
                />
              </Track>
              <OverlayTrigger placement="top" overlay={popover} trigger={['hover', 'focus', 'click']}>
                <span id="Edit-LicenseInformation--Verify-License-Hover-Icon" className="icon-stack">
                  <i className="icon icon-circle-o icon-stack-2x" />
                  <i className="icon icon-info icon-stack-1x" />
                </span>
              </OverlayTrigger>
            </div>
          </If>
        </div>
        <div id="License-Information-Edit-Or-Create-Container">
          <If condition={errorSaving}>
            <Alert bsStyle="danger">
              {errorSaving}
            </Alert>
          </If>
          <Choose>
            <When condition={successfullyVerified}>
              <Alert bsStyle="warning">
                This license has already been verified and additional changes cannot be made.
              </Alert>
            </When>
            <When condition={verificationPending}>
              <Alert bsStyle="warning">
                Verification for this license is still in progress and no changes can be made at
                this time.
              </Alert>
            </When>
          </Choose>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={12}>
              Select Category
              <div id="LicenseInformation-Edit--Categories" style={{ marginTop: 0 }}>
                <CategoryFilter
                  placeholder={currentCategories.length > 0 ? 'Add another category' : 'Add a category'}
                  categories={selectableCategories}
                  onCategorySearch={this.categoryAutocomplete}
                  disabled={pendingOrVerified}
                  selectSuggestion={(category) => {
                    this.selectSuggestion(category, maybeEditLicenseInfoId);
                  }}
                  style={{ paddingBottom: '15px' }}
                />
                <div style={{ float: 'left' }}>
                  <CategoryList
                    {...{
                      categories: currentCategories,
                      onCategoryClick: (category) => {
                        const updatedCategories =
                          this.state.selectedCategories.filter(cat => cat.id !== category.id);
                        this.setState({ selectedCategories: updatedCategories });
                      },
                      showXMark: true,
                      disabled: pendingOrVerified,
                    }}
                  />
                </div>
              </div>
            </Col>
          </Row>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={12}>
              <label className="control-label" htmlFor="Edit-License-Information--Dropdown-Menu">
                License Status
                <br />
                <DropdownButton
                  title={licenseSignatures[formValues.licenseSignature]}
                  id="Edit-License-Information--Dropdown-Menu"
                  bsSize="small"
                  label="License Status"
                  disabled={pendingOrVerified}
                >
                  {Object.keys(licenseSignatures).map(key => (
                    <MenuItem
                      id={key}
                      eventKey={key}
                      onSelect={value => change('licenseSignature', value)}
                    >
                      {licenseSignatures[key]}
                    </MenuItem>
                  ))}
                </DropdownButton>
              </label>
            </Col>
          </Row>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={12}>
              <ReduxForm.Input
                name="licenseName"
                id="EditOrCreateLicenseInformation--License-Name"
                type="text"
                label="License Name"
                disabled={pendingOrVerified}
              />
            </Col>
          </Row>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={6}>
              <ReduxForm.Input
                name="firstName"
                id="EditOrCreateLicenseInformation--First-Name"
                type="text"
                label="First Name"
                disabled={pendingOrVerified}
              />
            </Col>
            <Col xs={6}>
              <ReduxForm.Input
                name="lastName"
                id="EditOrCreateLicenseInformation--Last-Name"
                type="text"
                label="Last Name"
                disabled={pendingOrVerified}
              />
            </Col>
          </Row>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={12}>
              <ReduxForm.Input
                name="companyName"
                id="EditOrCreateLicenseInformation--Company-Name"
                type="text"
                label="Company"
                disabled={pendingOrVerified}
              />
            </Col>
          </Row>
          <Row className="LicenseInformation-Edit--Row">
            <Col xs={12}>
              <ReduxForm.Input
                name="licenseNumber"
                id="EditOrCreateLicenseInformation--License-Number"
                type="text"
                label="License Number"
                disabled={pendingOrVerified}
              />
            </Col>
          </Row>
          <Loader
            loading={statesLoading}
            element={
              <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                <i className="icon icon-load icon-spin icon-3x text-primary" />
              </Row>}
          >
            <Row className="LicenseInformation-Edit--Row">
              <Col xs={12}>
                <ReduxForm.Input
                  name="licenseLocale"
                  id="EditOrCreateLicenseInformation--License-Locale"
                  type="select"
                  label="State"
                  disabled={pendingOrVerified}
                  onChange={(e) => { this.updateState(e.target.value); }}
                >
                  <option value="" />
                  {orgStates.map(state => (
                    <option value={state.name}>{state.name}</option>
                  ))}
                </ReduxForm.Input>
              </Col>
            </Row>
            <Row className="LicenseInformation-Edit--Row">
              <Choose>
                <When condition={verificationActive}>
                  <Col xs={12}>
                    <ReduxForm.Input
                      name="licensingAuthorityExternalId"
                      id="EditOrCreateLicenseInformation--Authority-External-ID"
                      type="select"
                      label="Certifying Organization"
                      disabled={isEmpty(atlasOrgs) || orgsLoading || pendingOrVerified}
                      onChange={(e) => { this.updateOrg(e.target.value); }}
                    >
                      <option value="" />
                      {atlasOrgs.map(org => (
                        <option value={org.id}>{org.name}</option>
                  ))}
                    </ReduxForm.Input>
                  </Col>
                </When>
                <Otherwise>
                  <Col xs={12}>
                    <ReduxForm.Input
                      name="licenseAuthorityManualText"
                      id="EditOrCreateLicenseInformation--License-Authority"
                      type="text"
                      label="License Authority"
                    />
                  </Col>
                </Otherwise>
              </Choose>
            </Row>
            <If condition={!verificationActive}>
              <Row className="LicenseInformation-Edit--Row">
                <Col xs={12}>
                  <ReduxForm.Input
                    name="licenseExpiration"
                    id="EditOrCreateLicenseInformation--License-Expiration"
                    type="date"
                    label="License Expiration"
                  />
                </Col>
              </Row>
            </If>
            <Loader
              loading={orgFieldsLoading}
              element={
                <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                  <i className="icon icon-load icon-spin icon-3x text-primary" />
                </Row>}
            >
              {orgFields.map(field => (
                <If condition={verificationActive && !redundantOrgFields.includes(field.name)}>
                  <LicenseOrgField
                    name={field.name}
                    options={field.options}
                    verified={verificationActive}
                    disabled={pendingOrVerified}
                  />
                </If>
            ))}
            </Loader>
          </Loader>
          <Row className="text-right">
            <Button
              id="EditOrCreateLicenseInformation--Cancel"
              flat
              onClick={() => {
              sendEnhancedTrackEvent({
                name: 'Item Clicked',
                properties: {
                  ...defaultPageProps,
                  activityLocation: 'SP : My Business : License Details',
                  description: 'Cancel',
                },
              });
              updateLicenseReset();
              createLicenseReset();
              editCompletionFunction();
            }}
              style={{ marginRight: '30px' }}
            >
            Cancel
            </Button>
            <Button
              id="EditOrCreateLicenseInformation--Save"
              flat
              bsStyle="primary-office"
              type="submit"
              onClick={this.trackSaveEvent}
              disabled={isSaving || orgsLoading || orgFieldsLoading || pendingOrVerified}
              loading={isSaving}
            >
              <Choose>
                <When condition={verificationActive}>Verify</When>
                <Otherwise>Save</Otherwise>
              </Choose>
            </Button>
          </Row>
        </div>
      </Form>
    );
  }
}

const editOrCreateLicenseAnalytics = {
  page: {
    name: 'SP : My Business : License Details',
    properties: {
      ...defaultPageProps,
    },
  },
  elements: {
    'Edit-License-Information--Verify-License-Checkbox': itemClicked('SP : My Business : Licensed, Bonded, and Insured', 'Verify this license toggled'),
  },
};
export default compose(
  reduxForm({
    form: 'editOrCreateLicenseInformationForm',
    validate: validateCertification,
    // validation normally only triggers in case of certain changes, which our dynamic fields don't
    // appear to trigger on initial render. Override default behavior and make we re-validate on
    // any change/blur
    shouldError: () => true,
  }),
  connect(state => ({
    formValues: getFormValues('editOrCreateLicenseInformationForm')(state),
    profile: state.serviceProviderProfile,
    license: state.license,
  }), {
    ...analyticsActions,
    ...profileActions,
    ...licenseActions,
  }),
  analytics({
    ruleset: editOrCreateLicenseAnalytics,
  }),
  pure,
)(EditOrCreateLicenseInformation);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/EditOrCreateLicenseInformation.js